﻿import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  DatePicker,
  Empty,
  Flex,
  Grid,
  List,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ActivityRow } from '@titularis/api-client';
import { api } from '../api';
import { ACAO_LABEL, formatarData } from '../dominio';

/**
 * Trilha de auditoria (D-22, G-AUDIT).
 *
 * **Esta tela existe porque a trilha estava sendo gravada sem leitor.** A tela de
 * Atividade do PR-22 saiu quando o painel de Progresso a substituiu no PR-24, e
 * o painel responde outra pergunta: ele é leitura derivada de `question_versions`
 * e nunca toca a trilha. As rotas `/activity` seguiram testadas e sem uma única
 * chamada no frontend, o que deixava o G-AUDIT sem cumprir a sua função - poder
 * apontar responsável quando algo sumir.
 *
 * O que a trilha guarda é o que o histórico de versões **não** guarda: exclusão,
 * mudança de acesso e mudança de visibilidade. Edição de conteúdo não entra aqui
 * de propósito, porque `question_versions` já a guarda melhor.
 *
 * **No celular a tabela vira lista**, pelo mesmo motivo das demais telas do
 * admin: quatro colunas em 390px não encolhem, quebram. E a coluna que ficaria
 * de fora numa rolagem horizontal é a Descrição - justamente a que responde a
 * pergunta que traz alguém a esta tela, que é o que exatamente aconteceu.
 */

/** Cor por natureza da ação. Destrutivo salta à vista, e é o ponto. */
function corDaAcao(acao: string): string {
  if (/excluiu|descartou|revogou|desatualizou/.test(acao)) return 'red';
  if (/publicou|concedeu|reativou/.test(acao)) return 'green';
  if (/rascunho|anulou/.test(acao)) return 'orange';
  return 'default';
}

/*
 * Os rótulos moram no `dominio.ts` desde 07/09/2026, quando a tela de Produtos
 * passou a mostrar as alterações de um produto na própria página: a mesma ação
 * escrita em dois lugares ganharia dois nomes.
 */

/** Quantas linhas por página. Vale para os dois desenhos. */
const POR_PAGINA = 50;

export function TrilhaPage() {
  const telas = Grid.useBreakpoint();
  const compacto = !telas.md;
  const [pagina, setPagina] = useState(1);
  const [ator, setAtor] = useState<string | undefined>();
  const [acao, setAcao] = useState<string | undefined>();
  const [periodo, setPeriodo] = useState<[string, string] | undefined>();

  const atores = useQuery({
    queryKey: ['trilha', 'atores'],
    queryFn: () => api.listActivityActors(),
  });

  const acoes = useQuery({
    queryKey: ['trilha', 'acoes'],
    queryFn: () => api.listActivityActions(),
  });

  const trilha = useQuery({
    queryKey: ['trilha', 'linhas', { pagina, ator, acao, periodo }],
    queryFn: () =>
      api.listActivity({
        page: pagina,
        pageSize: POR_PAGINA,
        ...(ator ? { actorId: ator } : {}),
        ...(acao ? { action: acao } : {}),
        ...(periodo ? { de: periodo[0], ate: periodo[1] } : {}),
      }),
  });

  /** Quem agiu, com os dois casos em que não há nome. Serve aos dois desenhos. */
  const quem = (linha: ActivityRow) =>
    linha.actorName ?? (linha.actorId ? 'Conta excluída' : 'Cadastro Assistido');

  const paginacao = {
    current: pagina,
    pageSize: POR_PAGINA,
    /*
     * A rota devolve "tem mais", e não o total: contar tudo custaria varrer a
     * tabela inteira a cada página. O total inferido é o mínimo que a paginação
     * do Antd precisa para desenhar a próxima seta.
     */
    total: trilha.data?.temMais ? pagina * POR_PAGINA + 1 : pagina * POR_PAGINA,
    onChange: setPagina,
    showSizeChanger: false,
  };

  const vazio = <Empty description="Nenhuma ação no período." />;

  /*
   * No celular, cada linha vira um cartão - e a ordem muda junto com o desenho.
   *
   * Na tabela a data vem primeiro, porque a coluna fixa dá a varredura vertical.
   * Empilhada, ela roubaria a primeira linha de quem está procurando o que
   * aconteceu. Então a ação vem na frente, em etiqueta, e quando e quem descem
   * juntos para a linha de contexto.
   */
  const noCelular = (
    <List<ActivityRow>
      loading={trilha.isLoading}
      dataSource={trilha.data?.items ?? []}
      locale={{ emptyText: vazio }}
      pagination={paginacao}
      renderItem={(linha) => (
        <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
          {/*
            `minWidth: 0` é o que faz o texto longo caber em vez de esticar o
            cartão para fora da tela: é o mesmo detalhe da lista do Comentário
            Assistido, e sem ele a página inteira ganha rolagem horizontal.
          */}
          <Flex vertical gap={4} style={{ width: '100%', minWidth: 0 }}>
            <Tag
              color={corDaAcao(linha.action)}
              style={{ marginInlineEnd: 0, alignSelf: 'flex-start' }}
            >
              {ACAO_LABEL[linha.action] ?? linha.action}
            </Tag>
            <Typography.Text>{linha.summary}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {formatarData(linha.createdAt)} · {quem(linha)}
            </Typography.Text>
          </Flex>
        </List.Item>
      )}
    />
  );

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        {/* Nível 3 come duas linhas em 390px, como nas demais telas do admin. */}
        <Typography.Title level={compacto ? 4 : 3} style={{ marginBottom: 4 }}>
          Trilha
        </Typography.Title>
        <Typography.Text type="secondary">
          Quem apagou, quem mudou acesso e quem mudou o que o aluno vê. Edição de conteúdo não
          aparece aqui: ela vive no histórico de versões da questão, que guarda o texto inteiro.
        </Typography.Text>
      </div>

      <Card size={compacto ? 'small' : undefined}>
        {/*
          No celular cada filtro ocupa a linha inteira. Lado a lado em 390px o
          seletor de "O quê" ficaria com uns 90px, e "Voltou para rascunho" numa
          caixa desse tamanho não é escolha, é adivinhação.
        */}
        <Flex gap={12} wrap vertical={compacto} style={{ marginBottom: 16 }}>
          <Select
            allowClear
            placeholder="Quem"
            style={compacto ? { width: '100%' } : { minWidth: 220 }}
            loading={atores.isLoading}
            value={ator}
            onChange={(valor) => {
              setAtor(valor);
              setPagina(1);
            }}
            options={(atores.data ?? []).map((pessoa) => ({
              value: pessoa.id,
              // Conta excluída preserva a autoria sem o nome, como no histórico.
              label: pessoa.name ?? 'Conta excluída',
            }))}
          />
          <Select
            allowClear
            placeholder="O quê"
            style={compacto ? { width: '100%' } : { minWidth: 240 }}
            loading={acoes.isLoading}
            value={acao}
            onChange={(valor) => {
              setAcao(valor);
              setPagina(1);
            }}
            options={(acoes.data ?? []).map((nome) => ({
              value: nome,
              label: ACAO_LABEL[nome] ?? nome,
            }))}
          />
          <DatePicker.RangePicker
            aria-label="Período"
            style={compacto ? { width: '100%' } : undefined}
            onChange={(_, textos) => {
              setPeriodo(textos[0] && textos[1] ? [textos[0], textos[1]] : undefined);
              setPagina(1);
            }}
          />
        </Flex>

        {trilha.data?.items.length === 0 ? (
          vazio
        ) : compacto ? (
          noCelular
        ) : (
          <Table<ActivityRow>
            rowKey="id"
            loading={trilha.isLoading}
            dataSource={trilha.data?.items ?? []}
            pagination={paginacao}
            columns={[
              {
                title: 'Quando',
                dataIndex: 'createdAt',
                width: 160,
                render: (data: string) => formatarData(data),
              },
              {
                title: 'Quem',
                dataIndex: 'actorName',
                width: 200,
                render: (_: string | null, linha) => quem(linha),
              },
              {
                title: 'O quê',
                dataIndex: 'action',
                width: 200,
                render: (nome: string) => (
                  <Tag color={corDaAcao(nome)}>{ACAO_LABEL[nome] ?? nome}</Tag>
                ),
              },
              { title: 'Descrição', dataIndex: 'summary' },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}
